/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable, NgZone, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { filter, first, tap } from 'rxjs/operators';
import moment from 'moment';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { IGeoLocationService } from '@nimbus-global/features/geo-location/services/i-geo-location.service';
import { ILocation } from '@nimbus-global/features/shared/services/api/i-location.models';
import { GeoLocalizationStatus } from '@nimbus-global/features/permissions/models/global-permissions.model';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { CookieService } from '@nimbus-library/features/shared/services/cookies/cookies.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { IEnvironmentService } from '@nimbus-library/features/configuration/i-environment.service';

interface SelectedGeoLocation {
  userId: string;
  location: string;
}

@Injectable()
export class GeoLocationService extends IGeoLocationService {
  private readonly recentlySelectedKey: string = 'Nimbus.Location.RecentlySelected';

  public constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly environmentService: IEnvironmentService,
    private readonly permissionManagerService: IPermissionManagerService,
    @Optional() teamsContextAccessor: IMicrosoftTeamsContextAccessor,
    cookieService: CookieService,
    authenticationService: IAuthenticationService,
    environmentConfigurationService: EnvironmentConfigurationService,
    logger: ILogger,
  ) {
    super(
      `Nimbus.${environmentConfigurationService.configurationSettings.environmentName}.Location`,
      cookieService,
      authenticationService,
      environmentConfigurationService,
      teamsContextAccessor,
      logger,
    );
  }

  public resolveSelectedLocation(): void {
    const location = this.getSelectedLocation();
    if (location && location !== this.windowLocation.host && this.environmentService.environment.production) {
      const currentUrl = new URL(this.windowLocation.href);
      const url = new URL(`https://${location}`);
      url.pathname = currentUrl.pathname;
      url.search = currentUrl.search;
      this.saveRecentlySelectedLocation(location);
      this.windowLocation.href = url.toString();
    }
  }

  public redirectToLocation(location: ILocation): void {
    this.saveRecentlySelectedLocation(location.location);
    super.redirectToLocation(location, true);
  }

  public resolveLocation(redirectUrl: string, locationSelected: boolean): void {
    this.locationResolved$.next(null);

    if (redirectUrl === '/location') {
      redirectUrl = null;
    }

    this.permissionManagerService
      .getGlobalPermissionsModel$()
      .pipe(
        filter((pm) => !!pm),
        first(),
        tap((pm) => {
          let status = pm.getGeoLocationStatus(false);

          if (locationSelected) {
            status = GeoLocalizationStatus.NoActionRequired;
          }

          const decodedRedirectUrl = this.getDecodedRedirectUrl(redirectUrl);

          // Location selector works for provisioning only, with one exception: a user who has no
          // access on this cluster at all. LocationSelectorRequired here means PermissionManager
          // found no model for them and none of the tenant's locations is the current one, so
          // downgrading would end in "You are not allowed to view this page" with no way to reach
          // the cluster they actually live on. Bug #152705. This cannot loop: once they land there,
          // getGeoLocationStatus sees a current location (or an empty list) and returns
          // NoActionRequired.
          if (
            status === GeoLocalizationStatus.LocationSelectorRequired &&
            !decodedRedirectUrl.startsWith('/provisioning') &&
            pm.hasPermissionSet(NimbusPermissionSet.NimbusUser)
          ) {
            status = GeoLocalizationStatus.NoActionRequired;
          }

          this.locationResolved$.next(status);
          switch (status) {
            case GeoLocalizationStatus.RedirectRequired:
              this.redirectToLocation(pm.getRedirectLocation());
              break;
            case GeoLocalizationStatus.LocationSelectorRequired:
              this.ngZone.run(() =>
                this.router.navigate(['.', 'location'], {
                  queryParams: redirectUrl ? { returnUrl: redirectUrl } : {},
                  queryParamsHandling: 'merge',
                }),
              );
              break;
            case GeoLocalizationStatus.NoActionRequired:
              break;
            default:
              throw new Error(`GeoLocalizationStatus.${GeoLocalizationStatus[status]} is not supported`);
          }
        }),
      )
      .subscribe((pm) => {
        if (!this.teamsContextAccessor.isStandAlone() && !this.windowLocation.href.includes('provisioning')) {
          this.teamsContextAccessor.getContext$().subscribe(({ groupId }) => {
            if (groupId && !pm.teams[groupId]) {
              this.router.navigate(['.', 'wrong-location']);
            }
          });
        }
      });
  }

  public loadLocationInformation(): void {}

  protected getUrl(location: ILocation): URL {
    return new URL(`https://${location.location}/authentication/login`);
  }

  private getDecodedRedirectUrl(redirectUrl: string | null): string {
    if (!redirectUrl) {
      return '';
    }

    try {
      return decodeURIComponent(redirectUrl);
    } catch {
      this.logger?.warn?.(`Failed to decode redirectUrl '${redirectUrl}'. Falling back to raw value.`);
      return redirectUrl;
    }
  }

  private saveRecentlySelectedLocation(location: string): void {
    if (this.authenticationService.getUserInfo()) {
      this.cookieService.setCookie(
        this.recentlySelectedKey,
        JSON.stringify({ userId: this.authenticationService.getUserInfo().userId, location } as SelectedGeoLocation),
        moment().add(1, 'month'),
        { Domain: this.getRootDomain() },
      );
    }
  }
}
